import get from 'lodash/get';

/**
 * order-calc Stage 6.4: what checkout sends to amen, and the check of the
 * created order against the cart (spec §10, §13, catalogue D20).
 *
 * `orderCalc` is the integer module order (the `order` of the shared
 * calculation, order-calc-selector.js). When the module could not calculate the
 * cart there is no order, and CheckoutUtils creates no order for it.
 */

/**
 * The cartcalc fields for the create-order request: the integer total in
 * minor units, and the module version. Amen reads cartcalcTotal as minor units
 * when cartcalcVersion is present.
 * @param   {Object} orderCalc The module order.
 * @returns {Object}           { cartcalcTotal, cartcalcVersion }
 */
export const cartcalcOrderFields = orderCalc => ({
  cartcalcTotal: orderCalc.totals.total,
  cartcalcVersion: orderCalc.meta.version,
});

/**
 * The item ids of a list, one entry for each unit, sorted. Module lines and
 * order items both have one entry for each unit (`${itemId}#${n}` lines, and
 * amen Basket.toFlatArray), so equal lists mean the same items and quantities.
 * @param   {Array}  list The list (anything that is not an array gives []).
 * @param   {string} key  The item id field.
 * @returns {Array}       The sorted ids, as strings.
 */
const sortedItemIds = (list, key) => (Array.isArray(list) ? list : [])
  .map(entry => String(get(entry, key)))
  .sort();

/**
 * Compare the created order with the module cart, for the New Relic
 * createOrder event. Exact, in minor units, against the order total on the
 * same basis as amen cartDiscrepancy: payment.stotal (pre-tax) when tax is
 * added at checkout, payment.total when it is included.
 *   - totalsMatch false with versionSkew false: a parity defect.
 *   - versionSkew true: the order was calculated with another module version
 *     (deploy skew, spec §13). Do not count it.
 *   - itemsMatch false: amen priced other items than the cart. The basket
 *     changed after the cart loaded (for example in another tab), so this is
 *     not a parity defect. A parity defect has totalsMatch false, versionSkew
 *     false and itemsMatch true.
 *
 * The check only logs: it reads with get() and never throws, so it cannot stop
 * the checkout. The values are flat, for a New Relic page action.
 * @param   {Object}      orderCalc cart.orderCalc (undefined for a legacy cart).
 * @param   {Object}      order     The order from the create-order response.
 * @returns {Object|null}           The check fields, or null for a legacy cart.
 */
export const orderCalcCheck = (orderCalc, order) => {
  if (!orderCalc) {
    return null;
  }

  const cartcalcTotal = get(orderCalc, 'totals.total', null);
  const cartcalcVersion = get(orderCalc, 'meta.version', null);
  const orderCartTotal = get(order, get(order, 'report.isTaxExclusive') ? 'payment.stotal' : 'payment.total', null);
  const orderCalcVersion = get(order, 'calc.version', null);
  const cartItemIds = sortedItemIds(get(orderCalc, 'lines'), 'itemId');
  // Amen adds free product items outside the basket (FreeProducts.deliverProducts)
  const orderItems = get(order, 'items');
  const basketOrderItems = Array.isArray(orderItems) ? orderItems.filter(item => !get(item, 'isFreeProduct')) : [];
  const orderItemIds = sortedItemIds(basketOrderItems, '_id');

  return {
    cartcalcTotal,
    orderCartTotal,
    cartcalcVersion,
    orderCalcVersion,
    versionSkew: cartcalcVersion !== orderCalcVersion,
    // A missing total is not a match
    totalsMatch: Number.isInteger(cartcalcTotal) && cartcalcTotal === orderCartTotal,
    // An empty list is not a match
    itemsMatch: cartItemIds.length > 0
      && cartItemIds.length === orderItemIds.length
      && cartItemIds.every((id, i) => id === orderItemIds[i]),
  };
};
